<template>
  <div class="modal-body">
    <div class="modal-section">
      <h3 class="modal-heading">Välj vilket lån du vill ändra villkor för</h3>
      <AppTable>
        <thead>
          <tr>
            <th></th>
            <th>Lånenummer</th>
            <th>Lånebelopp</th>
            <th>Nuvarande villkor</th>
            <th>Kommande ränta</th>
            <th>Kommande villkorsperiod</th>
          </tr>
        </thead>
        <tbody>
          <AppTableRow v-for="loan in debt.loans" :key="loan.id" class="selectable" @click="selectedLoanId = loan.id">
            <AppTableCell action>
              <input
                class="selectable"
                type="radio"
                name="interest-rate-lock-loan"
                :checked="selectedLoanId === loan.id"
                :aria-label="`Välj lån ${loan.publicLoanNumber}`"
              />
            </AppTableCell>
            <AppTableCell nowrap>{{ loan.publicLoanNumber }}</AppTableCell>
            <AppTableCell nowrap>{{ formatAmountWithCurrency(loan.principal.current) }}</AppTableCell>
            <AppTableCell nowrap>
              {{ t(`INTEREST_TERM.${loan.interestTerm.interestTerm}`) }} ({{
                formatPercentage(loan.pricing.interestRate)
              }})
              <br />
              Villkorsperiod {{ formatShortDate(loan.loanTerms.startDate) }} –
              {{ formatShortDate(oneDayBefore(loan.loanTerms.nextLoanTermsChangeDate)) }}
            </AppTableCell>
            <AppTableCell nowrap>
              <template v-if="loan.interestTerm.nextInterestTerm">
                {{ t(`INTEREST_TERM.${loan.interestTerm.nextInterestTerm}`) }} från
                {{ formatShortDate(loan.interestTerm.nextInterestTermsChangeDate) }}
              </template>
              <template v-else>–</template>
            </AppTableCell>
            <AppTableCell nowrap>
              <template v-if="loan.loanTerms.upcoming">
                {{ formatShortDate(loan.loanTerms.upcoming.startDate) }} –
                {{ formatShortDate(oneDayBefore(loan.loanTerms.upcoming.endDate)) }}
              </template>
              <template v-else>–</template>
            </AppTableCell>
          </AppTableRow>
        </tbody>
      </AppTable>
    </div>

    <div v-if="selectedLoan" class="loan-settings-box">
      <h3 class="loan-settings-heading">Lånenummer {{ selectedLoan.publicLoanNumber }}</h3>

      <section class="loan-settings-section" aria-labelledby="interest-heading">
        <h4 id="interest-heading" class="loan-settings-subheading">Ränta</h4>

        <div v-if="hasFixedRate" class="fixed-rate-banner" role="note">
          <SvgIcon class="fixed-rate-banner-icon" :path="mdiAlertOutline" size="20" />
          <div>
            <p class="fixed-rate-banner-title">
              Lånet har bunden ränta t.o.m. {{ formatDateIfValid(oneDayBefore(nextChangeDate)) ?? '-' }}
            </p>
            <p>
              Om kunden vill bryta bindningstiden, kontakta först Borgo via Meddelanden för att ta reda på om kunden
              behöver betala ränteskillnadsersättning (RSE).
            </p>
          </div>
        </div>

        <AppSelect v-model="newInterestTerm" label="Ny räntebindningsperiod" :options="interestTermOptions" mandatory />

        <AppSelect
          v-model="interestTermStart"
          label="Gäller från och med"
          :options="interestTermStartOptions"
          mandatory
        />
      </section>

      <section class="loan-terms" aria-labelledby="loan-terms-label">
        <h4 id="loan-terms-label" class="loan-settings-subheading">Villkorsperiod</h4>
        <div class="loan-terms-tabs" role="tablist" aria-labelledby="loan-terms-label">
          <button
            v-for="option in loanTermsChoiceOptions"
            :id="`loan-terms-tab-${option.value}`"
            :key="option.value"
            type="button"
            role="tab"
            class="loan-terms-tab"
            :class="{ 'loan-terms-tab-active': loanTermsChoice === option.value }"
            :aria-selected="loanTermsChoice === option.value"
            aria-controls="loan-terms-panel"
            :disabled="option.disabled"
            @click="loanTermsChoice = option.value"
          >
            {{ option.text }}
          </button>
        </div>

        <div
          id="loan-terms-panel"
          class="loan-terms-panel"
          role="tabpanel"
          :aria-labelledby="`loan-terms-tab-${loanTermsChoice}`"
        >
          <p v-if="loanTermsChoice === 'keep'">{{ currentLoanTermsDescription }}</p>

          <template v-else>
            <AppSelect
              v-model="loanTermsLength"
              label="Villkorsperiod (så länge ev rabatt gäller)"
              :options="loanTermsLengthOptions"
              mandatory
            />
            <AppTextField
              v-model="discountPercent"
              label="Rabatt (%)"
              suffix="%"
              input-mode="decimal"
              placeholder="0,25"
              :error="!!discountError"
              :error-messages="discountError"
            />
            <AppTextField v-model="discountReason" label="Anledning till rabatt (valfritt)" :disabled="!discountBps" />
          </template>
        </div>
      </section>

      <p v-if="summary">{{ summary }}</p>
    </div>

    <div class="modal-actions">
      <AppButton @click="emit('close')">Avbryt</AppButton>
      <AppButton v-if="selectedLoan" color="green" :disabled="!canSubmit" :loading="isSubmitting" @click="submit">
        Spara
      </AppButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { mdiAlertOutline } from '@mdi/js'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatAmountWithCurrency, formatDateIfValid, formatMonths, formatPercentage } from '@staff/shared'
import { AppButton, AppSelect, AppTable, AppTableCell, AppTableRow, AppTextField, SvgIcon, notify } from '@staff/ui'
import {
  bindLoanInterestTerm,
  fetchDebtById,
  type LoanInterestTermEffectiveFrom,
  type LoanTermsPayload,
} from '@/services/debt'
import {
  MAX_LOAN_TERMS_DISCOUNT_BPS,
  MortgageInterestTermMonths,
  MortgageInterestTerms,
  allowedLoanTermsLengths,
  isMortgageInterestTerm,
  type LoanTermsLengthInMonths,
  type MortgageInterestTerm,
} from '@/types/interest.types'
import type { Debt, Loan } from '../debt.types'

type InterestTermStart = 'tomorrow' | 'next-change-date'
type LoanTermsChoice = 'keep' | 'new'

const props = defineProps<{
  debt: Debt
  onSaved: (debt: Debt) => void
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const { t } = useI18n()

const selectedLoanId = ref('')
const newInterestTerm = ref<MortgageInterestTerm | ''>('')
const interestTermStart = ref<InterestTermStart>('tomorrow')
const loanTermsChoice = ref<LoanTermsChoice>('keep')
// AppSelect values are strings; parsed back to a number on submit.
const loanTermsLength = ref('')
const discountPercent = ref('')
const discountReason = ref('')
const isSubmitting = ref(false)

const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD')

const oneDayBefore = (date: string | null) => (date ? dayjs(date).add(-1, 'day').format('YYYY-MM-DD') : null)
const formatShortDate = (date: string | null) => formatDateIfValid(date, 'D MMM -YY') ?? '–'
const formatDiscount = (bps: number) => `${formatPercentage(bps / 100)} rabatt`

const interestTermOptions = computed(() =>
  MortgageInterestTerms.map((interestTerm) => ({
    value: interestTerm,
    text: t(`INTEREST_TERM.${interestTerm}`),
  })),
)

const selectedLoan = computed(() => props.debt.loans.find((loan) => loan.id === selectedLoanId.value) ?? null)

const nextChangeDate = computed(() => selectedLoan.value?.interestTerm.nextInterestTermsChangeDate ?? null)

// A fixed rate (anything but 3 months) can be broken from tomorrow, but that may cost the customer
// ränteskillnadsersättning (RSE), which Borgo handles by hand. The next change date stays the default for it.
const isFixedRate = (loan: Loan) => loan.interestTerm.interestTerm !== 'threeMonth'

const hasFixedRate = computed(() => !!selectedLoan.value && isFixedRate(selectedLoan.value))

const currentLoanTerms = computed(() => selectedLoan.value?.loanTerms ?? null)

const nextLoanTermsChangeDate = computed(() => currentLoanTerms.value?.nextLoanTermsChangeDate ?? null)

// "Från och med imorgon" means the same thing as the next change date when that is tomorrow.
const interestTermStartOptions = computed(() => [
  ...(nextChangeDate.value === tomorrow ? [] : [{ text: 'Från och med imorgon', value: 'tomorrow' }]),
  {
    text: `Nästa ränteändringsdag (${formatDateIfValid(nextChangeDate.value) ?? 'saknas'})`,
    value: 'next-change-date',
    disabled: !nextChangeDate.value,
  },
])

const changeDate = computed(() => (interestTermStart.value === 'tomorrow' ? tomorrow : nextChangeDate.value))

// Same-day month arithmetic, like the API does for Borgo. The API has the final say and answers 400 if it disagrees.
const newInterestTermEndDate = computed(() =>
  changeDate.value && newInterestTerm.value
    ? dayjs(changeDate.value).add(MortgageInterestTermMonths[newInterestTerm.value], 'month').format('YYYY-MM-DD')
    : null,
)

// Keeping the current loan terms is only possible when they outlast the new interest term; an interest change
// from tomorrow always starts a new loan terms period.
const canKeepLoanTerms = computed(() => {
  const loanTermsEndDate = nextLoanTermsChangeDate.value
  if (!loanTermsEndDate || interestTermStart.value === 'tomorrow') return false
  return !newInterestTermEndDate.value || newInterestTermEndDate.value <= loanTermsEndDate
})

const loanTermsChoiceOptions = computed<{ text: string; value: LoanTermsChoice; disabled: boolean }[]>(() => [
  { text: 'Behåll nuvarande', value: 'keep', disabled: !canKeepLoanTerms.value },
  { text: 'Ange ny', value: 'new', disabled: false },
])

const currentLoanTermsDescription = computed(() => {
  const discount = currentLoanTerms.value?.discounts[0]
  const endDate = formatDateIfValid(oneDayBefore(nextLoanTermsChangeDate.value)) ?? '-'
  return `Nuvarande villkorsperiod gäller t.o.m. ${endDate}${discount ? ` med ${formatDiscount(discount.bps)}` : ', utan rabatt'}.`
})

const loanTermsLengthOptions = computed(() =>
  allowedLoanTermsLengths(newInterestTerm.value).map((months) => ({
    value: String(months),
    text: formatMonths(months),
  })),
)

const discountBps = computed(() => {
  const trimmed = discountPercent.value.trim()
  if (!trimmed) return 0
  const percent = Number(trimmed.replace(',', '.'))
  return Number.isFinite(percent) ? Math.round(percent * 100) : NaN
})

const discountError = computed(() =>
  Number.isNaN(discountBps.value) || discountBps.value < 0 || discountBps.value > MAX_LOAN_TERMS_DISCOUNT_BPS
    ? `Ange en rabatt mellan 0 och ${formatPercentage(MAX_LOAN_TERMS_DISCOUNT_BPS / 100)}`
    : '',
)

const newLoanTermsValid = computed(
  () => loanTermsChoice.value === 'keep' || (!!loanTermsLength.value && !discountError.value),
)

const canSubmit = computed(
  () =>
    !!selectedLoan.value &&
    !!newInterestTerm.value &&
    !!changeDate.value &&
    (loanTermsChoice.value === 'new' || canKeepLoanTerms.value) &&
    newLoanTermsValid.value &&
    !isSubmitting.value,
)

const interestTermEffectiveFrom = computed<LoanInterestTermEffectiveFrom>(() =>
  interestTermStart.value === 'tomorrow' ? 'tomorrow' : 'nextChangeDate',
)

const summary = computed(() => {
  if (!newInterestTerm.value || !changeDate.value) return ''

  const binding = `Från ${changeDate.value} får lånet ${t(`INTEREST_TERM.${newInterestTerm.value}`)} räntebindningstid`
  if (loanTermsChoice.value === 'keep') {
    const loanTerms = currentLoanTerms.value
    const discount = loanTerms?.discounts[0]
    return `${binding}. Nuvarande villkor behålls${discount ? `: ${formatDiscount(discount.bps)} t.o.m. ${oneDayBefore(loanTerms?.nextLoanTermsChangeDate ?? null)}` : ''}.`
  }

  const lengthInMonths = Number(loanTermsLength.value)
  const period = lengthInMonths
    ? ` Rabatten gäller under nya villkorsperioden ${changeDate.value} – ${oneDayBefore(dayjs(changeDate.value).add(lengthInMonths, 'month').format('YYYY-MM-DD'))} (${formatMonths(lengthInMonths)}).`
    : ''
  return `${binding} och ${formatDiscount(Number.isNaN(discountBps.value) ? 0 : discountBps.value)}.${period}`
})

// Prefill the interest change settings for the picked loan. Loan terms already scheduled
// are shown as new terms so that "Behåll" cannot remove them by accident.
watch(selectedLoan, (loan) => {
  loanTermsChoice.value = 'keep'
  loanTermsLength.value = ''
  discountPercent.value = ''
  discountReason.value = ''

  if (!loan) {
    newInterestTerm.value = ''
    interestTermStart.value = 'tomorrow'
    return
  }

  const bookedInterestTerm = loan.interestTerm.nextInterestTerm

  if (isMortgageInterestTerm(bookedInterestTerm)) {
    newInterestTerm.value = bookedInterestTerm
  } else if (bookedInterestTerm === null) {
    newInterestTerm.value = 'threeMonth'
  } else {
    // The loan carries a term this picker can't offer (e.g. tenYear). Leave the field empty so
    // Spara stays disabled instead of silently rebinding the loan to something the user never chose.
    newInterestTerm.value = ''
  }

  interestTermStart.value =
    isFixedRate(loan) || loan.interestTerm.nextInterestTermsChangeDate ? 'next-change-date' : 'tomorrow'
  loanTermsChoice.value = canKeepLoanTerms.value ? 'keep' : 'new'

  const upcoming = loan.loanTerms.upcoming
  if (upcoming) {
    loanTermsChoice.value = 'new'
    discountPercent.value = upcoming.discounts[0] ? (upcoming.discounts[0].bps / 100).toFixed(2).replace('.', ',') : ''
    discountReason.value = upcoming.discounts[0]?.reason ?? ''
  }

  loanTermsLength.value = loanTermsLengthOptions.value.length === 1 ? loanTermsLengthOptions.value[0].value : ''
})

watch(canKeepLoanTerms, (canKeep) => {
  if (!canKeep) loanTermsChoice.value = 'new'
})

// Each interest term offers a single loan terms length, so a new interest term picks it.
watch(
  loanTermsLengthOptions,
  (options) => {
    if (options.length === 1) loanTermsLength.value = options[0].value
    else if (loanTermsLength.value && !options.some((option) => option.value === loanTermsLength.value))
      loanTermsLength.value = ''
  },
  { immediate: true },
)

const submit = async () => {
  const loan = selectedLoan.value
  const nextInterestTerm = newInterestTerm.value
  if (!canSubmit.value || !loan || !nextInterestTerm) return

  const loanTerms: LoanTermsPayload = {
    lengthInMonths: Number(loanTermsLength.value) as LoanTermsLengthInMonths,
    discounts:
      discountBps.value > 0
        ? [{ bps: discountBps.value, ...(discountReason.value.trim() ? { reason: discountReason.value.trim() } : {}) }]
        : [],
  }
  isSubmitting.value = true

  try {
    await bindLoanInterestTerm(props.debt.id, loan.id, {
      interestTerm: {
        nextInterestTerm,
        effectiveFrom: interestTermEffectiveFrom.value,
      },
      ...(loanTermsChoice.value === 'new' ? { loanTerms } : {}),
    })
  } catch (error) {
    notify({
      type: 'error',
      title: 'Kunde inte spara räntebindningen',
      description: error instanceof Error ? error.message : 'Ett okänt fel uppstod.',
      dismissable: true,
    })
    return
  } finally {
    isSubmitting.value = false
  }

  notify({
    type: 'success',
    title: 'Räntebindningen sparades',
  })

  // The binding already succeeded; re-read the debt only to refresh the view.
  // fetchDebtById reports its own failure, so a stale view is the worst case.
  try {
    const refreshedDebt = await fetchDebtById(props.debt.id).catch(() => null)
    if (refreshedDebt) props.onSaved(refreshedDebt)
  } catch (_err) {
    notify({
      type: 'error',
      title: 'Räntebindningen sparades men sidan kunde inte uppdateras',
      description: 'Ladda om sidan för att se den nya räntebindningen.',
      dismissable: true,
    })
  }

  emit('close')
}
</script>

<style scoped lang="scss">
// A heading inside a modal, dialled down to body size.
.modal-heading {
  font-size: var(--font-size-sm);
  line-height: var(--font-size-sm-line-height);
  font-weight: var(--font-weight-semibold);
}

.selectable {
  cursor: pointer;
}

.modal-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-20);
}

.modal-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-8);
}

.fixed-rate-banner {
  display: flex;
  gap: var(--space-12);
  padding: var(--space-12) var(--space-16);
  border: 1px solid var(--color-warning-emphasis);
  border-radius: var(--radius-md);
  background-color: var(--color-warning-subtle);
  color: var(--color-warning-on-subtle);
}

.fixed-rate-banner-icon {
  flex-shrink: 0;
  color: var(--color-icon-warning);
}

.fixed-rate-banner-title {
  font-weight: var(--font-weight-semibold);
}

.loan-terms {
  display: flex;
  flex-direction: column;
}

.loan-settings-heading {
  font-size: var(--font-size-xl);
  line-height: var(--font-size-xl-line-height);
  font-weight: var(--font-weight-semibold);
}

.loan-settings-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
}

.loan-settings-subheading {
  font-size: var(--font-size-base);
  line-height: var(--font-size-base-line-height);
  font-weight: var(--font-weight-semibold);
}

.loan-terms .loan-settings-subheading {
  margin-bottom: var(--space-8);
}

.loan-terms-tabs {
  display: flex;
  gap: var(--space-4);
  border-bottom: 1px solid var(--color-border-default);
}

.loan-terms-tab {
  margin-bottom: -1px;
  padding: var(--space-8) var(--space-16);
  border: 1px solid transparent;
  border-radius: var(--radius-md) var(--radius-md) 0 0;
  background: none;
  color: var(--color-fg-muted);
  cursor: pointer;

  &:hover:not(:disabled, .loan-terms-tab-active) {
    background-color: var(--color-button-default-hover-bg);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.loan-terms-tab-active {
  border-color: var(--color-border-default);
  border-bottom-color: var(--color-canvas-default);
  background-color: var(--color-canvas-default);
  color: var(--color-brand-default);
  font-weight: var(--font-weight-semibold);
}

.loan-terms-panel {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
  padding: var(--space-16);
  border: 1px solid var(--color-border-default);
  border-top: none;
  border-radius: 0 0 var(--radius-md) var(--radius-md);
}

.loan-settings-box {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
  padding: var(--space-16);
  border: 1px solid var(--color-border-divider);
  border-radius: var(--radius-md);
}
</style>
